import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import type ParticipantCycleDataModel from '../models/ParticipantCycleDataModel';
import type CycleModel from '../models/CycleModel';
import Table from './Table';
import LoadingSpinner from './LoadingSpinner';

// eslint-disable-next-line complexity
const StudyParticipantReport = () => {
    const { studyId, participantId } = useParams();

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(false);

    const [selectedCycle, setSelectedCycle] = useState<
        CycleModel | undefined
    >();

    const [cycles, setCycles] = useState<CycleModel[]>([]);

    const [cycleData, setCycleData] = useState<
        ParticipantCycleDataModel | undefined
    >();

    useEffect(() => {
        const loadParticipantCycles = async () => {
            setLoading(true);
            if (participantId) {
                const { success, cycles } = await api.getParticipantCycles(
                    Number(participantId),
                );
                setCycles(cycles);
                setError(!success);

                if (cycles.length === 0) {
                    setSelectedCycle(undefined);
                    setLoading(false);
                } else {
                    setSelectedCycle(cycles[0]);
                }
            }
        };

        void loadParticipantCycles();
    }, [participantId]);

    useEffect(() => {
        const loadParticipantCycleData = async () => {
            if (selectedCycle) {
                setLoading(true);
                const { success, data } = await api.getParticipantCycleData(
                    Number(participantId),
                    selectedCycle.start,
                );
                setCycleData(success ? data : undefined);
                setError(!success);
                setLoading(false);
            }
        };

        void loadParticipantCycleData();
    }, [participantId, selectedCycle]);

    if (!participantId) {
        return null;
    }

    const addMetadataToData = (data: Array<Record<string, string>>) => {
        return data.map((item) => ({
            studyId,
            participantId,
            cycleStartDate: selectedCycle?.start,
            ...item,
        }));
    };

    const addMetadataToColumns = (columns: string[]) => {
        return ['studyId', 'participantId', 'cycleStartDate', ...columns];
    };

    return (
        <div>
            <div className="flex items-center">
                <div className="text-primary text-3xl pb-4">
                    Report for Participant {participantId}
                </div>
            </div>
            {loading && <LoadingSpinner size={30} />}
            {error && !loading && (
                <div className="text-primary text-xl">
                    Error: failed to load data
                </div>
            )}
            {!loading && !error && !selectedCycle && (
                <div className="text-primary text-xl">
                    No cycles found for this participant within their study
                    dates
                </div>
            )}
            {!loading && !error && selectedCycle && cycleData && (
                <>
                    <div className="flex flex-row flex-wrap gap-2 pb-4">
                        {cycles.map((cycle) => (
                            <button
                                key={cycle.start}
                                type="button"
                                className={`btn ${
                                    cycle.start === selectedCycle.start
                                        ? 'btn-primary'
                                        : 'btn-secondary text-primary'
                                }`}
                                onClick={() => {
                                    setSelectedCycle(cycle);
                                }}
                            >
                                {cycle.start} - {cycle.end}
                            </button>
                        ))}
                    </div>
                    <div className="text-primary">
                        <div>Cycle length: {cycleData.cycleLength} days</div>
                        <div className="mt-4 underline">Phase I End Days:</div>
                        <div>
                            Day 5/6 Rule:{' '}
                            {cycleData.phaseOneEndDays.fiveSix
                                ? `day ${cycleData.phaseOneEndDays.fiveSix}`
                                : 'N/A'}
                        </div>
                        <div>
                            Doering Rule:{' '}
                            {cycleData.phaseOneEndDays.doering
                                ? `day ${cycleData.phaseOneEndDays.doering}`
                                : 'N/A'}
                        </div>
                        <div>
                            Last Dry Day Rule:{' '}
                            {cycleData.phaseOneEndDays.lastDryDay
                                ? `day ${cycleData.phaseOneEndDays.lastDryDay}`
                                : 'N/A'}
                        </div>
                        <div>
                            Fertility Monitor Rule:{' '}
                            {cycleData.phaseOneEndDays.fertilityMonitor
                                ? `day ${cycleData.phaseOneEndDays.fertilityMonitor}`
                                : 'N/A'}
                        </div>
                        <div className="mt-4">
                            Selected Phase I Rule: {cycleData.phaseOneRule}
                        </div>
                        <div className="mt-4 underline">
                            Phase III Start Days:
                        </div>
                        <div>
                            ST Rule:{' '}
                            {cycleData.phaseThreeStartDays.st
                                ? `day ${cycleData.phaseThreeStartDays.st}`
                                : 'N/A'}
                        </div>
                        <div>
                            Temperature Only Rule:{' '}
                            {cycleData.phaseThreeStartDays.temperatureOnly
                                ? `day ${cycleData.phaseThreeStartDays.temperatureOnly}`
                                : 'N/A'}
                        </div>
                        <div>
                            Mucus Only Rule:{' '}
                            {cycleData.phaseThreeStartDays.mucusOnly
                                ? `day ${cycleData.phaseThreeStartDays.mucusOnly}`
                                : 'N/A'}
                        </div>
                        <div>
                            Post Hormonal Rule:{' '}
                            {cycleData.phaseThreeStartDays.postHormonal
                                ? `day ${cycleData.phaseThreeStartDays.postHormonal}`
                                : 'N/A'}
                        </div>
                        <div>
                            Fertility Monitor Rule:{' '}
                            {cycleData.phaseThreeStartDays.fertilityMonitor
                                ? `day ${cycleData.phaseThreeStartDays.fertilityMonitor}`
                                : 'N/A'}
                        </div>
                        <div className="mt-4">
                            Selected Phase III Rule: {cycleData.phaseThreeRule}
                        </div>
                        <div className="mt-4">
                            Peak Day (SymptoThermal):{' '}
                            {cycleData.stPeakDay
                                ? `day ${cycleData.stPeakDay}`
                                : 'N/A'}
                        </div>
                        <div>
                            Peak Days (Fertility Monitor):{' '}
                            {cycleData.fertilityMonitorPeakDays.length > 0
                                ? `days ${cycleData.fertilityMonitorPeakDays.join(
                                      ', ',
                                  )}`
                                : 'N/A'}
                        </div>
                    </div>
                    <div className="h-4" />
                    <Table
                        title="Cycle Answers"
                        data={addMetadataToData(cycleData.cycleAnswers.answers)}
                        columns={addMetadataToColumns(
                            cycleData.cycleAnswers.columns,
                        )}
                        csvFilename={`CycleAnswers_Cycle_${selectedCycle.start}_Participant_${participantId}.csv`}
                    />
                    <div className="h-4" />
                    <Table
                        title="Date Answers and Symptoms"
                        data={addMetadataToData(cycleData.dateData)}
                        columns={addMetadataToColumns(cycleData.dateColumns)}
                        csvFilename={`DateAnswersAndSymptoms_Cycle_${selectedCycle.start}_Participant_${participantId}.csv`}
                    />
                </>
            )}
        </div>
    );
};

export default StudyParticipantReport;
